import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Users, Plus, Copy, Mail, X, RefreshCcw, CheckCircle2, Clock, AlertCircle, ShieldCheck,
  MoreHorizontal, Trash2, UserCog, ArrowLeft, Building2, Lock, CalendarClock,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth.jsx";
import { OrgSettingsCard } from "@/components/team/OrgSettingsCard";

const STATUS_BADGE = {
  pending:  { label: "Pendiente",  cls: "bg-[#FAECE7] text-[#A14620] border-transparent", icon: Clock },
  accepted: { label: "Aceptada",   cls: "bg-[#E6F5EF] text-[#1D9E75] border-transparent", icon: CheckCircle2 },
  revoked:  { label: "Revocada",   cls: "bg-[#F1EFE7] text-[#7A786F] border-transparent", icon: X },
  expired:  { label: "Caducada",   cls: "bg-[#F1EFE7] text-[#7A786F] border-transparent", icon: AlertCircle },
};

export default function Team() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const targetOrg = params.get("org") || null; // override de superadmin

  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [orgInfo, setOrgInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const qs = targetOrg ? `?org_id=${targetOrg}` : "";

  const load = async () => {
    setLoading(true);
    try {
      const [m, i] = await Promise.all([
        api.get(`/team/members${qs}`),
        api.get(`/team/invitations${qs}`),
      ]);
      setMembers(m.data.items || []);
      setInvites(i.data.items || []);
      setOrgInfo({
        org_id: m.data.org_id,
        org_name: m.data.org_name,
        is_own_org: m.data.is_own_org !== false,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar el equipo");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [targetOrg]);

  const copyLink = async (url) => {
    await navigator.clipboard.writeText(url);
    toast.success("Enlace copiado");
  };
  const revoke = async (id) => {
    if (!window.confirm("¿Revocar esta invitación?")) return;
    try {
      await api.delete(`/team/invitations/${id}`);
      toast.success("Invitación revocada");
      load();
    } catch (e) { toast.error("Error al revocar"); }
  };
  const resend = async (id) => {
    try {
      const r = await api.post(`/team/invitations/${id}/resend`);
      if (r.data.ok) toast.success(r.data.extended ? "Email reenviado y caducidad renovada +7 días" : "Email reenviado");
      else toast.warning(`No se envió email: ${r.data.email_error || "revisa Resend"}. El enlace sigue activo.`);
      load();
    } catch (e) { toast.error("Error al reenviar"); }
  };

  const extendInvitation = async (id, days = 7) => {
    try {
      await api.post(`/team/invitations/${id}/extend`, { days });
      toast.success(`Caducidad renovada (+${days} días)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo renovar");
    }
  };

  const removeMember = async (member) => {
    if (!window.confirm(`¿Eliminar definitivamente a ${member.name || member.email} del equipo?\n\nSus expedientes se mantendrán y se reasignarán a ti.\n\nUsa "Suspender" en su lugar si quieres impedir el acceso sin borrar al usuario.`)) return;
    try {
      await api.delete(`/team/members/${member.id}`);
      toast.success(`${member.name || member.email} eliminado del equipo`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const toggleSuspend = async (member) => {
    const next = !member.suspended;
    const action = next ? "suspender" : "reactivar";
    if (!window.confirm(`¿${next ? "Suspender" : "Reactivar"} a ${member.name || member.email}?\n\n${next ? "No podrá iniciar sesión hasta que lo reactives. Sus expedientes y datos se mantienen intactos." : "Volverá a poder iniciar sesión con normalidad."}`)) return;
    try {
      await api.patch(`/team/members/${member.id}`, { suspended: next });
      toast.success(`${member.name || member.email} ${next ? "suspendido" : "reactivado"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `No se pudo ${action}`);
    }
  };

  const changeRole = async (member, newRole) => {
    try {
      await api.patch(`/team/members/${member.id}`, { role: newRole });
      toast.success("Rol actualizado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar");
    }
  };

  const isSuper = user?.role === "superadmin";
  const isAdmin = user?.role === "admin" || isSuper;
  const otherOrg = !orgInfo?.is_own_org;

  return (
    <div className="space-y-8" data-testid="team-root">
      {otherOrg && (
        <Link to="/admin/organizaciones" className="text-sm text-[#7A786F] hover:text-[#2C2C2A] inline-flex items-center">
          <ArrowLeft className="h-4 w-4 mr-1" /> Volver a Organizaciones
        </Link>
      )}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          {otherOrg && (
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#D85A30] mb-1">
              <Building2 className="h-3 w-3" /> Gestionando otra organización
            </div>
          )}
          <h1 className="font-heading text-4xl font-bold tracking-tight text-[#2C2C2A]">Equipo</h1>
          <p className="text-[#7A786F] mt-1">
            Miembros e invitaciones de <b className="text-[#2C2C2A]">{orgInfo?.org_name || user?.org_name || "tu gabinete"}</b>.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-eks-primary rounded-[12px]" data-testid="invite-perito-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Invitar perito
            </Button>
          </DialogTrigger>
          <InviteDialog targetOrg={targetOrg} onCreated={() => { load(); setOpen(false); }} />
        </Dialog>
      </div>

      {/* Configuración IA del gabinete (solo su propio gabinete) */}
      {!otherOrg && (
        <OrgSettingsCard canEdit={isAdmin} />
      )}

      {/* Miembros */}
      <Section
        title={`Miembros · ${members.length}`}
        subtitle={otherOrg ? "Estás gestionando otra organización como superadmin." : "Personas con cuenta activa en tu gabinete."}
      >
        {loading ? (
          <Empty>Cargando…</Empty>
        ) : members.length === 0 ? (
          <Empty>Aún no hay miembros.</Empty>
        ) : (
          <div className="divide-y divide-[#E5E2D8]">
            {members.map((m) => {
              const isMe = m.id === user?.id;
              const canManage = !isMe && (isSuper || (user?.role === "admin" && orgInfo?.is_own_org));
              return (
                <div key={m.id} className="flex items-center justify-between gap-4 py-4 first:pt-0 last:pb-0" data-testid={`member-${m.email}`}>
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <Avatar name={m.name} />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-[#2C2C2A] truncate flex items-center gap-2">
                        {m.name}
                        {isMe && <span className="text-[10px] uppercase tracking-wider text-[#1D9E75] bg-[#E6F5EF] rounded-full px-2 py-0.5 font-semibold">Tú</span>}
                        {m.suspended && <span className="text-[10px] uppercase tracking-wider text-[#A32D2D] bg-[#FAECE7] rounded-full px-2 py-0.5 font-semibold" data-testid={`member-suspended-${m.email}`}>Suspendido</span>}
                      </div>
                      <div className="text-xs text-[#7A786F] truncate">{m.email}</div>
                    </div>
                  </div>
                  <Badge variant="outline" className={`rounded-full font-medium ${
                    m.role === "superadmin" ? "bg-[#FAECE7] text-[#D85A30] border-transparent" :
                    m.role === "admin" ? "bg-[#FAECE7] text-[#D85A30] border-transparent" :
                    m.role === "tramitador" ? "bg-[#EAF1FB] text-[#2E5AAC] border-transparent" :
                    "bg-[#FBFAF6] text-[#2C2C2A] border-[#E5E2D8]"
                  }`}>
                    {m.role === "superadmin" ? <><ShieldCheck className="h-3 w-3 mr-1" /> Superadmin</> :
                     m.role === "admin"      ? <><ShieldCheck className="h-3 w-3 mr-1" /> Administrador</> :
                     m.role === "tramitador" ? <><UserCog className="h-3 w-3 mr-1" /> Tramitador</> :
                                                "Perito"}
                  </Badge>
                  {canManage && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="h-8 w-8 rounded-[10px] border border-[#E5E2D8] hover:bg-[#FAECE7] flex items-center justify-center" data-testid={`member-menu-${m.email}`}>
                          <MoreHorizontal className="h-4 w-4 text-[#7A786F]" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        <DropdownMenuLabel className="text-xs">Cambiar rol</DropdownMenuLabel>
                        {m.role !== "admin" && (
                          <DropdownMenuItem onClick={() => changeRole(m, "admin")} data-testid={`promote-admin-${m.email}`}>
                            <ShieldCheck className="h-3.5 w-3.5 mr-2" /> Hacer administrador
                          </DropdownMenuItem>
                        )}
                        {m.role !== "perito" && (
                          <DropdownMenuItem onClick={() => changeRole(m, "perito")} data-testid={`demote-perito-${m.email}`}>
                            <UserCog className="h-3.5 w-3.5 mr-2" /> Hacer perito
                          </DropdownMenuItem>
                        )}
                        {m.role !== "tramitador" && (
                          <DropdownMenuItem onClick={() => changeRole(m, "tramitador")} data-testid={`demote-tramitador-${m.email}`}>
                            <UserCog className="h-3.5 w-3.5 mr-2" /> Hacer tramitador
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel className="text-xs">Acceso</DropdownMenuLabel>
                        {!isMe && (
                          <DropdownMenuItem onClick={() => toggleSuspend(m)} data-testid={`toggle-suspend-${m.email}`}>
                            {m.suspended
                              ? <><ShieldCheck className="h-3.5 w-3.5 mr-2 text-[#1D9E75]" /> Reactivar usuario</>
                              : <><Lock className="h-3.5 w-3.5 mr-2 text-[#A32D2D]" /> Suspender usuario</>}
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => removeMember(m)} className="text-[#A32D2D] focus:text-[#A32D2D] focus:bg-[#FAECE7]" data-testid={`remove-member-${m.email}`}>
                          <Trash2 className="h-3.5 w-3.5 mr-2" /> Eliminar definitivamente
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* Invitaciones */}
      <Section
        title={`Invitaciones · ${invites.length}`}
        subtitle="Enlaces enviados que aún no se han usado o aceptado."
      >
        {loading ? (
          <Empty>Cargando…</Empty>
        ) : invites.length === 0 ? (
          <Empty>No hay invitaciones todavía. Invita al primer perito de tu equipo.</Empty>
        ) : (
          <div className="divide-y divide-[#E5E2D8]">
            {invites.map((inv) => {
              const st = STATUS_BADGE[inv.status] || STATUS_BADGE.pending;
              const StIcon = st.icon;
              return (
                <div key={inv.id} className="flex items-center justify-between gap-4 py-4 first:pt-0 last:pb-0 flex-wrap" data-testid={`invite-${inv.email}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-[#2C2C2A] truncate">{inv.name || inv.email}</div>
                    <div className="text-xs text-[#7A786F] truncate">{inv.email}</div>
                    <div className="text-[11px] text-[#7A786F] mt-1 flex items-center gap-2 flex-wrap">
                      {inv.email_sent ? (
                        <span className="inline-flex items-center text-[#1D9E75]"><Mail className="h-3 w-3 mr-1" /> Email enviado</span>
                      ) : (
                        <span className="inline-flex items-center text-[#A14620]" title={inv.email_error || ""}>
                          <AlertCircle className="h-3 w-3 mr-1" /> Email no enviado
                        </span>
                      )}
                      <span>· Caduca {new Date(inv.expires_at).toLocaleDateString("es-ES")}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={`rounded-full font-medium ${st.cls}`}>
                      <StIcon className="h-3 w-3 mr-1" /> {st.label}
                    </Badge>
                    {(inv.status === "pending" || inv.status === "expired") && (
                      <>
                        {inv.status === "pending" && (
                          <Button size="sm" variant="outline" className="border-[#E5E2D8] rounded-[10px] hover:bg-[#FAECE7]" onClick={() => copyLink(inv.accept_url)} data-testid={`copy-invite-${inv.email}`}>
                            <Copy className="h-3.5 w-3.5 mr-1" /> Copiar enlace
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="border-[#E5E2D8] rounded-[10px] hover:bg-[#FAECE7]" onClick={() => extendInvitation(inv.id, 7)} data-testid={`extend-invite-${inv.email}`}>
                          <CalendarClock className="h-3.5 w-3.5 mr-1" /> Renovar caducidad
                        </Button>
                        <Button size="sm" variant="outline" className="border-[#E5E2D8] rounded-[10px] hover:bg-[#FAECE7]" onClick={() => resend(inv.id)} data-testid={`resend-invite-${inv.email}`}>
                          <RefreshCcw className="h-3.5 w-3.5 mr-1" /> Reenviar
                        </Button>
                        <Button size="sm" variant="outline" className="border-[#E5E2D8] rounded-[10px] text-[#A32D2D] hover:bg-[#FAECE7] hover:text-[#A32D2D]" onClick={() => revoke(inv.id)} data-testid={`revoke-invite-${inv.email}`}>
                          <X className="h-3.5 w-3.5 mr-1" /> Revocar
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Section>
    </div>
  );
}

function InviteDialog({ onCreated, targetOrg }) {
  const [form, setForm] = useState({ email: "", name: "", role: "perito" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const qs = targetOrg ? `?org_id=${targetOrg}` : "";
      const r = await api.post(`/team/invitations${qs}`, form);
      setResult(r.data);
      if (r.data.email_sent) toast.success("Invitación enviada por email");
      else toast.warning("Invitación creada · el email no se ha enviado, copia el enlace.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al invitar");
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <DialogContent data-testid="invite-result-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Invitación creada</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#7A786F]">
          {result.email_sent
            ? `Hemos enviado un email a ${result.email}. También puedes pasarle el enlace directo:`
            : `El email no se pudo enviar (${result.email_error || "Resend no configurado"}). Copia el enlace y pásaselo al perito:`}
        </p>
        <div className="bg-[#FBFAF6] border border-[#E5E2D8] rounded-[12px] p-3 flex items-center justify-between gap-2">
          <code className="text-xs text-[#2C2C2A] break-all font-mono">{result.accept_url}</code>
          <Button size="icon" variant="outline" className="h-8 w-8 border-[#E5E2D8] shrink-0" onClick={() => { navigator.clipboard.writeText(result.accept_url); toast.success("Copiado"); }} data-testid="copy-invite-link-modal">
            <Copy className="h-3.5 w-3.5" />
          </Button>
        </div>
        <DialogFooter>
          <Button onClick={() => onCreated()} className="btn-eks-primary rounded-[12px]" data-testid="invite-done-btn">Hecho</Button>
        </DialogFooter>
      </DialogContent>
    );
  }

  return (
    <DialogContent data-testid="invite-dialog">
      <DialogHeader>
        <DialogTitle className="font-heading">Invitar miembro al equipo</DialogTitle>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="inv-email">Email *</Label>
          <Input id="inv-email" type="email" required value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="usuario@gabinete.es"
            className="rounded-[12px] border-[#E5E2D8]"
            data-testid="invite-email-input"
          />
          <p className="text-[11px] text-[#7A786F] mt-1">Debe coincidir con su email en Ekspertium para vincular la cuenta.</p>
        </div>
        <div>
          <Label htmlFor="inv-name">Nombre (opcional)</Label>
          <Input id="inv-name" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="María García"
            className="rounded-[12px] border-[#E5E2D8]"
            data-testid="invite-name-input"
          />
        </div>
        <div>
          <Label htmlFor="inv-role">Rol</Label>
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger id="inv-role" className="rounded-[12px] border-[#E5E2D8]" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="perito">Perito · Ve y peritar sus expedientes</SelectItem>
              <SelectItem value="tramitador">Tramitador · Gestiona expedientes telemáticos (sin videollamada)</SelectItem>
              <SelectItem value="admin">Administrador · Ve todo, invita</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button type="submit" disabled={loading} className="btn-eks-primary rounded-[12px]" data-testid="invite-submit-btn">
            {loading ? "Enviando…" : "Enviar invitación"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="bg-white border border-[#E5E2D8] rounded-[14px] p-6">
      <div className="mb-4">
        <h2 className="font-heading text-lg font-semibold text-[#2C2C2A] flex items-center gap-2">
          <Users className="h-4 w-4 text-[#D85A30]" /> {title}
        </h2>
        {subtitle && <p className="text-xs text-[#7A786F] mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function Empty({ children }) {
  return <div className="text-sm text-[#7A786F] py-6 text-center">{children}</div>;
}

function Avatar({ name }) {
  const initials = (name || "?")
    .split(/\s+/).slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");
  return (
    <div className="h-9 w-9 rounded-full bg-[#FAECE7] text-[#D85A30] font-heading font-semibold text-sm flex items-center justify-center shrink-0">
      {initials || "·"}
    </div>
  );
}
